import React, { useEffect, useCallback, useMemo, useRef } from "react";
import { useViewerStore } from "@/stores/viewer/viewer-store";
import { Component, ComponentType, Position, Size } from "@/types";

interface ViewerPerformanceOptimizerProps {
  children: React.ReactNode;
  viewportBounds?: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
}

interface OptimizationStats {
  totalComponents: number;
  renderedComponents: number;
  culledComponents: number;
  memoryUsage: number;
  lastUpdateTime: number;
}

// Virtual scrolling and component culling for large projects
export const ViewerPerformanceOptimizer: React.FC<
  ViewerPerformanceOptimizerProps
> = ({ children, viewportBounds }) => {
  const {
    components,
    zoom,
    enableVirtualization,
    visibleBounds,
    setVisibleBounds,
  } = useViewerStore();

  const containerRef = useRef<HTMLDivElement>(null);
  const [optimizationStats, setOptimizationStats] =
    React.useState<OptimizationStats>({
      totalComponents: 0,
      renderedComponents: 0,
      culledComponents: 0,
      memoryUsage: 0,
      lastUpdateTime: Date.now(),
    });

  // Calculate visible bounds based on scroll position and zoom
  const updateVisibleBounds = useCallback(() => {
    if (!containerRef.current || !enableVirtualization) {
      setVisibleBounds(null);
      return;
    }

    const container = containerRef.current;
    const rect = container.getBoundingClientRect();
    const scrollLeft = container.scrollLeft;
    const scrollTop = container.scrollTop;

    // Add buffer around visible area for smoother scrolling
    const bufferSize = Math.max(rect.width, rect.height) * 0.5;

    const bounds = {
      x: (scrollLeft - bufferSize) / zoom,
      y: (scrollTop - bufferSize) / zoom,
      width: (rect.width + bufferSize * 2) / zoom,
      height: (rect.height + bufferSize * 2) / zoom,
    };

    setVisibleBounds(bounds);
  }, [zoom, enableVirtualization, setVisibleBounds]);

  // Throttled scroll handler for performance
  const throttledScrollHandler = useMemo(() => {
    let timeoutId: NodeJS.Timeout;

    return () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(updateVisibleBounds, 16); // ~60fps
    };
  }, [updateVisibleBounds]);

  // Set up scroll listener
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !enableVirtualization) return;

    container.addEventListener("scroll", throttledScrollHandler);
    window.addEventListener("resize", updateVisibleBounds);

    // Initial bounds calculation
    updateVisibleBounds();

    return () => {
      container.removeEventListener("scroll", throttledScrollHandler);
      window.removeEventListener("resize", updateVisibleBounds);
    };
  }, [enableVirtualization, throttledScrollHandler, updateVisibleBounds]);

  // Update stats when components change
  useEffect(() => {
    const visibleComponents = visibleBounds
      ? components.filter((component) =>
          isComponentInBounds(component, visibleBounds),
        )
      : components;

    setOptimizationStats({
      totalComponents: components.length,
      renderedComponents: visibleComponents.length,
      culledComponents: components.length - visibleComponents.length,
      memoryUsage: calculateMemoryUsage(visibleComponents),
      lastUpdateTime: Date.now(),
    });
  }, [components, visibleBounds]);

  return (
    <div
      ref={containerRef}
      className="w-full h-full overflow-y-auto overflow-x-hidden viewer-performance-container"
    >
      {children}
    </div>
  );
};

// Utility function to check if component is within visible bounds
function isComponentInBounds(
  component: Component,
  bounds: { x: number; y: number; width: number; height: number },
): boolean {
  // Skip components without position or size (e.g., children in flex containers)
  if (!component.position || !component.size) {
    return true; // Include components without bounds (they'll be handled by flex layout)
  }

  const width = typeof component.size.width === 'number' ? component.size.width : 0;
  const height = typeof component.size.height === 'number' ? component.size.height : 0;

  // Include components with non-numeric dimensions
  if (width === 0 || height === 0) {
    return true;
  }

  const compLeft = component.position.x ?? 0;
  const compTop = component.position.y ?? 0;
  const compRight = compLeft + width;
  const compBottom = compTop + height;

  const boundsRight = bounds.x + bounds.width;
  const boundsBottom = bounds.y + bounds.height;

  // Check if component intersects with visible bounds
  return !(
    compRight < bounds.x ||
    compLeft > boundsRight ||
    compBottom < bounds.y ||
    compTop > boundsBottom
  );
}

// Estimate memory usage based on component complexity
function calculateMemoryUsage(components: Component[]): number {
  return components.reduce((total, component) => {
    // Base component overhead
    let size = 1; // 1KB base

    // Add size based on component type and properties
    switch (component.type) {
      case ComponentType.IMAGE:
        size += 5; // Images are heavier
        break;
      case ComponentType.CHART:
      case ComponentType.BAR_CHART:
      case ComponentType.LINE_CHART:
      case ComponentType.PIE_CHART:
      case ComponentType.AREA_CHART:
      case ComponentType.SCATTER_CHART:
        size += 3; // Charts have more data
        break;
      case ComponentType.TABLE:
      case ComponentType.ADVANCED_TABLE:
        size += 2; // Tables can be complex
        break;
      case ComponentType.RADIAL_DIAL:
        size += 2; // SVG arc rendering + interaction handlers
        break;
      case ComponentType.TOGGLE_SWITCH:
        size += 1; // Simple CSS toggle
        break;
      case ComponentType.STATUS_LIGHT:
        size += 1; // SVG indicator with optional animation
        break;
      case ComponentType.PUSH_BUTTON:
        size += 2; // SVG glow filter + press animation
        break;
      case ComponentType.ICON_BUTTON:
        size += 1; // Simple icon + CSS
        break;
      case ComponentType.STEPPER:
        size += 1; // Simple CSS layout
        break;
      case ComponentType.SLIDER:
        size += 1; // CSS track + thumb + optional SVG ticks
        break;
      case ComponentType.LEVEL_INDICATOR:
        size += 1; // SVG segments
        break;
        case ComponentType.SEGMENTED_CONTROL:
          size += 2;
          break;
        case ComponentType.COLOR_PICKER:
          size += 3;
          break;
        case ComponentType.THERMOSTAT:
          size += 3;
          break;
      default:
        size += 0.5;
    }

    // Add size based on component size (larger components use more memory for rendering)
    if (component.size) {
      const width = typeof component.size.width === 'number' ? component.size.width : 0;
      const height = typeof component.size.height === 'number' ? component.size.height : 0;
      const area = width * height;
      size += area / 50000; // Rough estimation
    }

    return total + size;
  }, 0);
}

// Hook for accessing performance optimization controls
export const useViewerPerformanceOptimization = () => {
  const {
    enableVirtualization: enabled,
    visibleBounds,
    components,
  } = useViewerStore();

  const enableVirtualization = useCallback((enable: boolean) => {
    useViewerStore.getState().setVirtualizationEnabled(enable);
  }, []);

  const getVisibleComponents = useCallback(() => {
    if (!enabled || !visibleBounds) {
      return components.filter((c) => !c.hidden && !c.isDeleted);
    }

    return components.filter((component) => {
      if (component.hidden || component.isDeleted) return false;
      return isComponentInBounds(component, visibleBounds);
    });
  }, [components, enabled, visibleBounds]);

  const getPerformanceStats = useCallback(() => {
    const visibleComponents = getVisibleComponents();

    return {
      totalComponents: components.length,
      visibleComponents: visibleComponents.length,
      culledComponents: components.length - visibleComponents.length,
      cullRatio:
        components.length > 0
          ? (components.length - visibleComponents.length) / components.length
          : 0,
      memoryEstimate: calculateMemoryUsage(visibleComponents),
      virtualizationEnabled: enabled,
    };
  }, [components.length, getVisibleComponents, enabled]);

  return {
    enabled,
    visibleBounds,
    enableVirtualization,
    getVisibleComponents,
    getPerformanceStats,
  };
};
